import * as store from '../app/store.js';
import { Scenario, ComparisonResult, MonthRow, ScenarioSummary, ScheduleResult } from '../domain/types.js';
import { buildSchedule } from '../domain/schedule.js';
import { summarize } from '../domain/metrics.js';
import { ShareError, decodeShareToken, extractShareToken } from '../features/persistence/share.js';
import {
  FormState,
  populateFormFromScenario,
  readFormState,
  buildScenario,
} from './form-utils.js';
import { attachMoneyFormattingAll, attachPercentFormatting } from './input-format.js';
import { ScenarioList, duplicateScenario, nextScenarioName } from './scenario-list.js';
import { initDatePickers } from './date-picker.js';
import { initLumpList } from './lump-list.js';
import { syncSheetStatuses, syncAllDateConstraints, renderClampNotes } from './form-constraints.js';
import { renderLog } from './summary.js';
import { renderResult, setResultStale } from './result.js';
import { initCompare, renderCompareEntries, compareEntries, CompareEntry } from './compare.js';
import { initEventChips, syncEventChips } from './event-chips.js';
import { renderSaveStatus } from './save-status.js';
import { renderPaymentsTable } from './payments-table.js';
import { renderFormErrors } from './form-errors.js';
import { renderCharts, initChartTabs, BalanceSeries } from './charts/index.js';
import { initFullscreen, FullscreenContext } from './fullscreen.js';
import { initKpiBar } from './kpi-bar.js';
import { formatMonthLabel } from './format.js';
import { enterViewMode, initViewMode, leaveView, showShareError } from './view-mode.js';
import { openShareDialog } from './share-dialog.js';
import { initBankCalculation } from './bank-calculation.js';
import { sampleRecalculation, setRecalculations } from './recalculation-list.js';

let currentState: FormState | null = null;
let currentScenarioId: string | null = null;
let currentBaselineId: string | undefined = undefined;
let currentComparisonKey = '';
let scenarioList: ScenarioList | null = null;
let recomputePending = false;
// «Пример расчёта…» stays until the first edit, and only when nothing was stored before.
let sampleNoteVisible = false;
/** The shared scenario on screen and the visitor's own selection at that moment. */
let viewing: { scenario: Scenario; ownKey: string } | null = null;

/** Selection, baseline and comparison of the visitor's own scenarios, as one comparable string. */
function ownStateKey(state: store.StoreState): string {
  return [state.lastViewedId ?? '', state.baselineId ?? '', state.comparisonIds.join(',')].join('|');
}

type Rendered = { scenario: Scenario; schedule: ScheduleResult; summary: ScenarioSummary; data: ComparisonData };
/** What is on screen now; the fullscreen chart and «Поделиться» read it. */
let lastRendered: Rendered | null = null;

init();

function init(): void {
  const form = document.getElementById('scenario-form');
  if (!(form instanceof HTMLFormElement)) return;

  store.load();
  const state = store.getState();
  const token = extractShareToken(location.hash);
  sampleNoteVisible = state.scenarios.length === 0 && !token;
  const loadedScenario = state.lastViewedId
    ? state.scenarios.find(s => s.id === state.lastViewedId)
    : undefined;

  currentBaselineId = state.baselineId;
  currentComparisonKey = state.comparisonIds.join('|');

  // The link takes the form; the visitor's own scenario stays in the store untouched.
  if (loadedScenario && !token) {
    currentScenarioId = loadedScenario.id;
    populateFormFromScenario(form, loadedScenario);
  }

  scenarioList = new ScenarioList('scenario-drawer', 'scenario-drawer-backdrop', 'scenario-list');
  store.subscribe(newState => onStoreChange(form, newState));

  bindTopbar();
  bindForm(form);
  initBankCalculation(form, () => scheduleRecompute(form));
  // The built-in example (no saved scenario) recalculates the payment like the bank does.
  if (!loadedScenario) setRecalculations(form, [sampleRecalculation(form)]);
  initEventChips(form);
  initCompare(createComparisonCopy);
  initChartTabs();
  initKpiBar();
  initFullscreen(getChartContext);
  initDatePickers(form);
  initViewMode({ onSave: saveSharedScenario, onClose: () => leaveView() });
  syncOverpaymentVisibility(form);
  syncMoreDetails();
  window.addEventListener('hashchange', () => {
    if (extractShareToken(location.hash)) location.reload();
  });

  if (token) {
    void openSharedScenario(form, token);
  } else {
    // Nothing changed yet: compute without writing, so a visitor who only looks at the
    // built-in example stores nothing (and sees the example again after a reload).
    recompute(form, false);
  }
}

function onStoreChange(form: HTMLFormElement, newState: store.StoreState): void {
  if (viewing) {
    // Selecting, re-basing or comparing the visitor's own scenarios ends the read-only view
    // (the reload shows the result); nothing else is rendered while a link is on screen.
    if (ownStateKey(newState) !== viewing.ownKey) leaveView();
    renderSaveStatus(newState.saveStatus);
    return;
  }

  if (newState.lastViewedId && newState.lastViewedId !== currentScenarioId) {
    const next = newState.scenarios.find(sc => sc.id === newState.lastViewedId);
    if (next) {
      currentScenarioId = next.id;
      populateFormFromScenario(form, next);
      // populateForm dispatches input/change → scheduleRecompute → upsert → notify.
    }
  }

  const comparisonKey = newState.comparisonIds.join('|');
  if (newState.baselineId !== currentBaselineId || comparisonKey !== currentComparisonKey) {
    const current = newState.scenarios.find(sc => sc.id === currentScenarioId);
    // Only a rendered change is consumed; otherwise the next notification re-renders it.
    if (current) {
      currentBaselineId = newState.baselineId;
      currentComparisonKey = comparisonKey;
      if (!currentState) currentState = readFormState(form, null);
      refreshUI(current);
    }
  }

  renderSaveStatus(newState.saveStatus);
}

/** Start-up with `#s=<token>`: decode, show the scenario read-only, never touch the store. */
async function openSharedScenario(form: HTMLFormElement, token: string): Promise<void> {
  let shared: Scenario;
  let schedule: ScheduleResult;
  try {
    shared = await decodeShareToken(token);
    schedule = buildSchedule(shared);
  } catch (error) {
    showShareError(error instanceof ShareError ? error.code : 'corrupt');
    // A reload must not show the error again: the broken token leaves the address bar.
    history.replaceState(null, '', `${location.pathname}${location.search}`);
    const own = store.getState();
    const loaded = own.lastViewedId ? own.scenarios.find(s => s.id === own.lastViewedId) : undefined;
    if (loaded) {
      currentScenarioId = loaded.id;
      populateFormFromScenario(form, loaded);
    } else {
      // A fresh visitor with a broken link still sees the built-in example and its note.
      sampleNoteVisible = own.scenarios.length === 0;
    }
    recompute(form, false);
    return;
  }
  viewing = { scenario: shared, ownKey: ownStateKey(store.getState()) };
  populateFormFromScenario(form, shared);
  currentState = readFormState(form, null);
  syncSheetStatuses(form);
  syncEventChips(form);
  enterViewMode(form);
  const summary = summarize(shared, schedule.months);
  const entries = compareEntries({ currentId: shared.id, comparisonIds: [], summaries: [summary] });
  renderAll({ scenario: shared, schedule, summary, data: { entries, others: [] } });
}

/** «Сохранить себе»: an editable copy joins the visitor's scenarios and the page reloads on it. */
function saveSharedScenario(): void {
  if (!viewing) return;
  const copy: Scenario = { ...structuredClone(viewing.scenario), id: crypto.randomUUID() };
  viewing = null;
  store.upsertScenario(copy);
  store.flushPendingSaves();
  leaveView();
}

function bindTopbar(): void {
  document.getElementById('open-scenarios')?.addEventListener('click', () => {
    scenarioList?.open();
  });
  // «Поделиться» shares what is on screen: the form's scenario, or the shared one in the read-only view.
  document.getElementById('share-current')?.addEventListener('click', () => {
    const scenario = lastRendered?.scenario;
    if (scenario) void openShareDialog(scenario);
  });
}

function inputNamed(form: HTMLFormElement, name: string): HTMLInputElement | null {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field : null;
}

function setDisplay(id: string, visible: boolean): void {
  const el = document.getElementById(id);
  if (el) el.style.display = visible ? '' : 'none';
}

/** Immediate visibility toggle for the overpayment type (the lump list manages its own). */
function syncOverpaymentVisibility(form: HTMLFormElement): void {
  const type = form.querySelector<HTMLInputElement>('input[name="overpaymentType"]:checked')?.value ?? 'fixed';
  setDisplay('fixed-overpayment-fields', type === 'fixed');
  setDisplay('adaptive-overpayment-fields', type === 'adaptive');
}

/**
 * «Ещё: тип платежа, название» is a collapsible only on phones; on wider screens the
 * details stay open and CSS hides the summary.
 */
function syncMoreDetails(): void {
  const more = document.getElementById('form-more');
  if (!(more instanceof HTMLDetailsElement) || typeof window.matchMedia !== 'function') return;
  const phone = window.matchMedia('(max-width: 640px)');
  const apply = () => { more.open = !phone.matches; };
  apply();
  phone.addEventListener('change', apply);
}

function bindForm(form: HTMLFormElement): void {
  form.addEventListener('input', () => scheduleRecompute(form));
  form.addEventListener('change', () => scheduleRecompute(form));

  // Money inputs get thousands separators while typing (lump rows attach their own on creation);
  // percent inputs show «16,9 %» when not focused. Fallbacks mirror readFormState.
  attachMoneyFormattingAll(form);
  const percentFallbacks: Array<[string, number]> = [['rateApr', 10], ['discountRate', 6.5], ['refinanceRate', 8.5]];
  percentFallbacks.forEach(([name, fallback]) => {
    const input = inputNamed(form, name);
    if (input) attachPercentFormatting(input, { fallback, min: 0, max: 50 });
  });

  form.querySelectorAll('input[name="overpaymentType"]').forEach(control => {
    control.addEventListener('change', () => syncOverpaymentVisibility(form));
  });

  initLumpList(form, () => scheduleRecompute(form));
}

/** Coalesces `input` and `change` bursts into one recompute per animation frame. */
function scheduleRecompute(form: HTMLFormElement): void {
  if (viewing) return;
  // Any user edit (or switching scenarios) replaces the built-in example.
  sampleNoteVisible = false;
  if (recomputePending) return;
  recomputePending = true;
  const run = () => {
    recomputePending = false;
    recompute(form);
  };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
  else setTimeout(run, 0);
}

/** Reads the form, updates the store and re-renders; `persist` is false only for the initial load. */
function recompute(form: HTMLFormElement, persist = true): void {
  if (viewing) return;
  currentState = readFormState(form, currentState);
  syncSheetStatuses(form);
  syncEventChips(form);
  // Clamping a date changes the form, so read it again before building the scenario.
  const clamped = syncAllDateConstraints(form, currentState);
  renderClampNotes(form, clamped, formatMonthLabel);
  if (clamped.length > 0) currentState = readFormState(form, currentState);
  const scenario = buildScenario(currentState);

  if (!currentScenarioId) currentScenarioId = crypto.randomUUID();
  scenario.id = currentScenarioId;

  // The store validates and returns errors without mutating state; keep the previous
  // results on screen (marked stale) and show the reasons instead of silently going stale.
  const { issues } = store.upsertScenario(scenario, persist);
  renderFormErrors(issues);
  setResultStale(issues.length > 0);
  if (issues.length > 0) return;
  refreshUI(scenario);
}

type ComparisonData = {
  entries: CompareEntry[];
  /** Lines for the balance chart: every entry that is neither current nor baseline. */
  others: BalanceSeries[];
  baselineMonths?: MonthRow[];
  comparison?: ComparisonResult;
};

function comparisonFor(scenarioId: string): ComparisonData {
  const { baselineId, comparisonIds } = store.getState();
  const entries = compareEntries({ currentId: scenarioId, baselineId, comparisonIds, summaries: store.listSummaries() });
  const others: BalanceSeries[] = [];
  entries.forEach(entry => {
    if (entry.isCurrent || entry.isBaseline) return;
    const schedule = store.getSchedule(entry.summary.scenarioId);
    if (schedule) others.push({ name: entry.summary.name, months: schedule.months, color: entry.color });
  });
  const hasBaseline = Boolean(baselineId) && baselineId !== scenarioId;
  return {
    entries,
    others,
    baselineMonths: hasBaseline ? store.getSchedule(baselineId!)?.months : undefined,
    comparison: hasBaseline ? store.compareScenarios(baselineId!, scenarioId) : undefined,
  };
}

function refreshUI(scenario: Scenario): void {
  const schedule = store.getSchedule(scenario.id);
  const summary = store.listSummaries().find(s => s.scenarioId === scenario.id);
  if (!schedule || !summary || !currentState) return;

  renderAll({ scenario, schedule, summary, data: comparisonFor(scenario.id) });
}

function renderAll(rendered: Rendered): void {
  lastRendered = rendered;
  // «Поделиться» starts disabled in the markup: there is nothing to share before the first render.
  const share = document.getElementById('share-current');
  if (share instanceof HTMLButtonElement) share.disabled = false;
  const { scenario, schedule, summary, data } = rendered;
  if (!currentState) return;
  const isBaseline = store.getState().baselineId === scenario.id;
  renderResult({ summary, schedule, scenario, state: currentState, comparison: data.comparison, isBaseline });
  renderSampleNote();
  renderLog(currentState, summary);
  renderCharts({ schedule, scenario, baselineMonths: data.baselineMonths, others: data.others });
  renderCompareEntries(data.entries);
  renderPaymentsTable(schedule);
}

function renderSampleNote(): void {
  const note = document.getElementById('sample-note');
  if (note) note.hidden = !sampleNoteVisible;
}

/** «Создать копию для сравнения»: the form's scenario becomes the baseline and its copy is opened. */
function createComparisonCopy(): void {
  if (!currentScenarioId) return;
  store.setBaseline(currentScenarioId);
  duplicateScenario(currentScenarioId, nextScenarioName());
}

function getChartContext(): FullscreenContext | null {
  if (!lastRendered) return null;
  const { scenario, schedule, data } = lastRendered;
  return { schedule, scenario, baselineMonths: data.baselineMonths, others: data.others };
}

